import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, Bed, Star, Heart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function PropertyCard({ property, isWishlisted, onWishlistChange }) {
  const { user } = useAuth();

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || user === false) {
      toast.error("Please sign in to save properties");
      return;
    }
    try {
      const { data } = await api.post("/wishlist/toggle", { property_id: property.id });
      onWishlistChange?.(property.id, data.in_wishlist);
      toast.success(data.in_wishlist ? "Saved to wishlist" : "Removed from wishlist");
    } catch {
      toast.error("Could not update wishlist");
    }
  };

  const mainImg = property.images?.[0] || "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200";

  return (
    <Link
      to={`/property/${property.id}`}
      data-testid={`property-card-${property.id}`}
      className="group block fade-up"
    >
      <div className="relative card-image rounded-md">
        <div className="aspect-[4/3] sm:aspect-[4/5] overflow-hidden rounded-md">
          <img
            src={mainImg}
            alt={property.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <button
          onClick={toggleWishlist}
          className="absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center bg-white/85 backdrop-blur-md hover:bg-white transition-colors"
          data-testid={`wishlist-btn-${property.id}`}
          aria-label="Save"
        >
          <Heart className={`h-4 w-4 ${isWishlisted ? "fill-[hsl(10_51%_55%)] text-[hsl(10_51%_55%)]" : "text-[hsl(90_4%_10%)]"}`} />
        </button>
        {property.featured && (
          <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-[hsl(165_37%_17%)] text-white font-medium">
            Signature
          </span>
        )}
      </div>

      <div className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-serif-display text-lg leading-tight truncate">{property.title}</h3>
            <p className="text-sm text-[hsl(100_3%_30%)] flex items-center gap-1 mt-1">
              <MapPin className="h-3.5 w-3.5" />
              <span className="truncate">{property.address}</span>
            </p>
          </div>
          {property.rating > 0 && (
            <div className="flex items-center gap-1 text-sm shrink-0">
              <Star className="h-3.5 w-3.5 fill-[hsl(10_51%_55%)] text-[hsl(10_51%_55%)]" />
              <span className="font-medium">{property.rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-2 text-xs text-[hsl(100_3%_30%)]">
          <span className="flex items-center gap-1"><Bed className="h-3 w-3" /> {property.bedrooms} bed</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Users className="h-3 w-3" /> up to {property.max_guests}</span>
          <span>·</span>
          <span>{property.property_type}</span>
        </div>

        <div className="mt-3 flex items-baseline gap-1.5">
          <span className="font-serif-display text-xl">₹{property.price_per_night.toLocaleString("en-IN")}</span>
          <span className="text-sm text-[hsl(100_3%_30%)]">/ night</span>
        </div>
      </div>
    </Link>
  );
}

export default PropertyCard;
